/**
 * RNC — DeliveryLog
 *
 * Paginated NotificationLog view via `apiClient.retail.notifications.listLog`.
 * Friendly-stub when the route returns 404/501 (still PLANNED per wiring digest).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, FilterChips, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import type {
  NotificationChannel,
  NotificationStatus,
} from '@retail-ops/shared-types/enums';
import type { NotificationLogDto } from '@retail-ops/shared-types';

type ChannelFilter = 'all' | NotificationChannel;

function statusVariant(status: NotificationStatus): 'ok' | 'warn' | 'err' | 'info' | 'muted' {
  switch (status) {
    case 'delivered':
    case 'sent':
    case 'read':
      return 'ok';
    case 'queued':
      return 'info';
    case 'failed':
    case 'bounced':
      return 'err';
    case 'opted-out':
      return 'muted';
    default:
      return 'warn';
  }
}

export default function DeliveryLog(): JSX.Element {
  const client = useApiClient();
  const navigate = useNavigate();
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [rows, setRows] = useState<ReadonlyArray<NotificationLogDto>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [notImplemented, setNotImplemented] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setNotImplemented(false);

    client.retail.notifications
      .listLog(
        { top: 50, ...(channel !== 'all' ? { channel } : {}) },
        { signal: ctrl.signal },
      )
      .then((res) => {
        if (ctrl.signal.aborted) return;
        if (!res.success) {
          setNotImplemented(true);
          setRows([]);
        } else {
          setRows(res.logs ?? []);
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        const msg = err instanceof Error ? err.message : '';
        const is501 = /\b(501|404|not[- ]implemented|not[- ]found)\b/i.test(msg);
        if (is501) {
          setNotImplemented(true);
          setRows([]);
        } else {
          setError(msg || 'Unable to load delivery log.');
        }
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [client, channel]);

  return (
    <div className="rnc-page">
      <header className="rnc-page__header">
        <div className="rnc-page__title-row">
          <h1 className="rnc-page__title">Delivery log</h1>
          <Pill variant="muted">7-year retention</Pill>
          {notImplemented && <Pill variant="warn">Route planned</Pill>}
        </div>
        <p className="rnc-page__subtitle">
          Every outbound notification is logged with a SHA-256 content hash
          (per digest §RNC — PII stays out of the log).
        </p>
      </header>

      <div style={{ display: 'flex', gap: 'var(--sp-6)', alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterChips
          label="Channel"
          options={[
            { value: 'all', label: 'All' },
            { value: 'email', label: 'Email' },
            { value: 'teams', label: 'Teams' },
            { value: 'sms', label: 'SMS' },
            { value: 'physical', label: 'Physical' },
          ]}
          selected={channel}
          onChange={(v) => setChannel(v as ChannelFilter)}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Button variant="ghost" size="sm" onClick={() => navigate('/rnc')}>
            Back to dashboard
          </Button>
        </div>
      </div>

      <section className="rnc-panel">
        {loading ? (
          <div>Loading delivery log…</div>
        ) : error ? (
          <div className="rnc-banner rnc-banner--err">{error}</div>
        ) : notImplemented ? (
          <div className="rnc-banner rnc-banner--warn">
            The <code>/v3/notifications/log</code> route is planned. Once it
            ships, recent notifications will appear here with status,
            channel, and content-hash.
          </div>
        ) : rows.length === 0 ? (
          <div className="rnc-banner">No delivery rows in the current window.</div>
        ) : (
          <div>
            {rows.map((r) => (
              <div key={r.notificationId} className="rnc-log-row">
                <span className="rnc-log-row__channel">{r.channel}</span>
                <span>
                  <strong style={{ color: 'var(--ink-900)' }}>{r.templateName}</strong>
                  {r.recipientAddress && (
                    <span style={{ color: 'var(--ink-600)' }}>
                      {' → '}{maskRecipient(r.recipientAddress)}
                    </span>
                  )}
                </span>
                <span className="rnc-log-row__status">
                  <Pill variant={statusVariant(r.status)}>{r.status}</Pill>
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>
                  v{r.templateVersion ?? '—'}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>
                  {r.sentAt?.slice(0, 16).replace('T', ' ') ?? r.queuedAt.slice(0, 16).replace('T', ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function maskRecipient(addr: string): string {
  if (addr.includes('@')) {
    const [local, domain] = addr.split('@');
    if (!local || !domain) return addr;
    return `${local.slice(0, 2)}***@${domain}`;
  }
  // Phone number
  const digits = addr.replace(/\D/g, '');
  if (digits.length >= 4) return `***${digits.slice(-4)}`;
  return '***';
}
